import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
    ArrowLeft, Check, Shield, Zap, Star, Crown, RefreshCw,
    Monitor, AlertTriangle, Brain, FileText, CreditCard, TrendingUp,
    CheckCircle, XCircle, Loader2
} from 'lucide-react';
import { subscriptionAPI, paymentsAPI } from '@/lib/api';
import { LOGO_URL } from '@/lib/brand';

const Subscription = () => {
    const [searchParams] = useSearchParams();
    const [subscription, setSubscription] = useState(null);
    const [plans, setPlans] = useState([]);
    const [usage, setUsage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedBilling, setSelectedBilling] = useState('monthly');
    const [processingPlan, setProcessingPlan] = useState(null);
    const [paymentStatus, setPaymentStatus] = useState(null);

    useEffect(() => {
        loadData();
        checkPaymentStatus();
    }, []);

    const checkPaymentStatus = async () => {
        const sessionId = searchParams.get('session_id');
        const success = searchParams.get('success');
        const cancelled = searchParams.get('cancelled');

        if (cancelled) {
            setPaymentStatus({ type: 'cancelled', message: 'Payment was cancelled.' });
            return;
        }

        if (sessionId && success) {
            setPaymentStatus({ type: 'checking', message: 'Verifying payment...' });
            
            // Poll for payment status
            let attempts = 0;
            const maxAttempts = 10;
            
            const pollStatus = async () => {
                try {
                    const response = await paymentsAPI.getStatus(sessionId);
                    
                    if (response.data.payment_status === 'paid') {
                        setPaymentStatus({ 
                            type: 'success', 
                            message: `Payment successful! You are now on the ${response.data.plan_name} plan.` 
                        });
                        loadData(); // Reload subscription data
                        return;
                    } else if (response.data.status === 'expired') {
                        setPaymentStatus({ type: 'error', message: 'Payment session expired.' });
                        return;
                    }
                    
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(pollStatus, 2000);
                    } else {
                        setPaymentStatus({ type: 'error', message: 'Payment verification timed out.' });
                    }
                } catch (error) {
                    console.error('Error checking payment:', error);
                    setPaymentStatus({ type: 'error', message: 'Error verifying payment.' });
                }
            };
            
            pollStatus();
        }
    };

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [subRes, plansRes, usageRes] = await Promise.all([
                subscriptionAPI.get(),
                subscriptionAPI.getPlans(),
                subscriptionAPI.getUsage()
            ]);
            setSubscription(subRes.data);
            setPlans(plansRes.data.plans);
            setUsage(usageRes.data);
        } catch (error) {
            console.error('Error loading subscription data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const getPlanIcon = (planId) => {
        switch (planId) {
            case 'free': return Shield;
            case 'starter': return Zap;
            case 'professional': return Star;
            case 'enterprise': return Crown;
            default: return Shield;
        }
    };

    const getPlanColor = (planId) => {
        switch (planId) {
            case 'free': return 'text-gray-400';
            case 'starter': return 'text-cyan-400';
            case 'professional': return 'text-purple-400';
            case 'enterprise': return 'text-yellow-400';
            default: return 'text-gray-400';
        }
    };

    const [selectedDeviceCount, setSelectedDeviceCount] = useState(1);
    const [showDeviceSelector, setShowDeviceSelector] = useState(false);
    const [selectedPlanForCheckout, setSelectedPlanForCheckout] = useState(null);

    const getPlanLimits = (planId) => {
        switch (planId) {
            case 'starter': return { min: 1, max: 25 };
            case 'professional': return { min: 1, max: 100 };
            case 'enterprise': return { min: 1, max: 999 }; // Unlimited
            default: return { min: 1, max: 25 };
        }
    };

    const handleUpgrade = async (planId) => {
        if (planId === 'free') return;
        
        // Reset device count and show selector modal
        setSelectedDeviceCount(1);
        setSelectedPlanForCheckout(planId);
        setShowDeviceSelector(true);
    };

    const incrementDevices = () => {
        const limits = getPlanLimits(selectedPlanForCheckout);
        if (selectedDeviceCount < limits.max) {
            setSelectedDeviceCount(prev => prev + 1);
        }
    };

    const decrementDevices = () => {
        if (selectedDeviceCount > 1) {
            setSelectedDeviceCount(prev => prev - 1);
        }
    };

    const proceedToCheckout = async () => {
        setProcessingPlan(selectedPlanForCheckout);
        setShowDeviceSelector(false);
        try {
            const response = await paymentsAPI.createCheckout(selectedPlanForCheckout, selectedDeviceCount);
            
            if (response.data.checkout_url) {
                // Redirect to Stripe checkout
                window.location.href = response.data.checkout_url;
            }
        } catch (error) {
            console.error('Error creating checkout:', error);
            alert('Failed to start checkout. Please try again.');
        } finally {
            setProcessingPlan(null);
            setSelectedPlanForCheckout(null);
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
                <RefreshCw className="w-12 h-12 text-cyan-400 animate-spin" />
            </div>
        );
    }

    const getPlanPrice = (planId) => {
        const prices = { starter: 8, professional: 15, enterprise: 25 };
        return prices[planId] || 0;
    };

    const getPlanName = (planId) => {
        const names = { starter: 'Starter', professional: 'Professional', enterprise: 'Enterprise' };
        return names[planId] || '';
    };

    return (
        <div className="min-h-screen bg-[#0a0a0f] text-white">
            {/* Device Count Selector Modal */}
            {showDeviceSelector && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#0d0d14] rounded-2xl p-6 max-w-md w-full border border-white/10">
                        <h3 className="text-xl font-bold mb-2">{getPlanName(selectedPlanForCheckout)} Plan</h3>
                        <p className="text-white/60 text-sm mb-6">
                            How many devices do you want to protect?
                            {selectedPlanForCheckout === 'enterprise' 
                                ? ' (Unlimited devices available)' 
                                : ` (Max ${getPlanLimits(selectedPlanForCheckout).max} devices)`}
                        </p>
                        
                        {/* Up/Down Counter */}
                        <div className="flex items-center justify-center gap-6 mb-6">
                            <button
                                onClick={decrementDevices}
                                disabled={selectedDeviceCount <= 1}
                                className={`w-14 h-14 rounded-xl text-2xl font-bold transition-all ${
                                    selectedDeviceCount <= 1 
                                        ? 'bg-white/5 text-white/20 cursor-not-allowed' 
                                        : 'bg-white/10 text-white hover:bg-white/20'
                                }`}
                            >
                                −
                            </button>
                            <div className="text-center">
                                <div className="text-5xl font-bold text-cyan-400">{selectedDeviceCount}</div>
                                <div className="text-sm text-white/40 mt-1">device{selectedDeviceCount > 1 ? 's' : ''}</div>
                            </div>
                            <button
                                onClick={incrementDevices}
                                disabled={selectedDeviceCount >= getPlanLimits(selectedPlanForCheckout).max}
                                className={`w-14 h-14 rounded-xl text-2xl font-bold transition-all ${
                                    selectedDeviceCount >= getPlanLimits(selectedPlanForCheckout).max 
                                        ? 'bg-white/5 text-white/20 cursor-not-allowed' 
                                        : 'bg-white/10 text-white hover:bg-white/20'
                                }`}
                            >
                                +
                            </button>
                        </div>

                        {/* Quick Select Buttons */}
                        <div className="flex justify-center gap-2 mb-6">
                            {[1, 5, 10, selectedPlanForCheckout === 'starter' ? 25 : selectedPlanForCheckout === 'professional' ? 50 : 100]
                                .filter(n => n <= getPlanLimits(selectedPlanForCheckout).max)
                                .map((count) => (
                                <button
                                    key={count}
                                    onClick={() => setSelectedDeviceCount(count)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                        selectedDeviceCount === count 
                                            ? 'bg-cyan-500 text-white' 
                                            : 'bg-white/10 text-white/60 hover:bg-white/20'
                                    }`}
                                >
                                    {count}
                                </button>
                            ))}
                        </div>

                        <div className="bg-white/5 rounded-lg p-4 mb-6">
                            <div className="flex justify-between items-center">
                                <span className="text-white/60">Monthly Total:</span>
                                <span className="text-2xl font-bold text-cyan-400">
                                    ${getPlanPrice(selectedPlanForCheckout) * selectedDeviceCount}/mo
                                </span>
                            </div>
                            <div className="text-sm text-white/40 mt-1">
                                {selectedDeviceCount} device{selectedDeviceCount > 1 ? 's' : ''} × ${getPlanPrice(selectedPlanForCheckout)}/device
                            </div>
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeviceSelector(false)}
                                className="flex-1 py-3 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={proceedToCheckout}
                                disabled={processingPlan}
                                className="flex-1 py-3 rounded-lg bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-bold hover:from-cyan-600 hover:to-purple-700 transition-all"
                            >
                                {processingPlan ? 'Processing...' : 'Continue to Payment'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Header */}
            <header className="border-b border-white/5 bg-[#0d0d14]">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white">
                            <ArrowLeft className="w-5 h-5" />
                            Back to Dashboard
                        </Link>
                    </div>
                    <Link to="/" className="flex items-center gap-3">
                        <img src={LOGO_URL} alt="Shiro Security" className="w-8 h-8 object-contain" />
                        <span className="text-lg font-bold gradient-text">SHIRO</span>
                    </Link>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold mb-2">Subscription & Billing</h1>
                    <p className="text-white/60">Manage your subscription and view usage statistics</p>
                </div>

                {/* Payment Status Alert */}
                {paymentStatus && (
                    <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                        paymentStatus.type === 'success' ? 'bg-green-500/20 text-green-400' :
                        paymentStatus.type === 'error' ? 'bg-red-500/20 text-red-400' :
                        paymentStatus.type === 'cancelled' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-cyan-500/20 text-cyan-400'
                    }`}>
                        {paymentStatus.type === 'success' && <CheckCircle className="w-5 h-5" />}
                        {paymentStatus.type === 'error' && <XCircle className="w-5 h-5" />}
                        {paymentStatus.type === 'cancelled' && <XCircle className="w-5 h-5" />}
                        {paymentStatus.type === 'checking' && <Loader2 className="w-5 h-5 animate-spin" />}
                        {paymentStatus.message}
                    </div>
                )}

                {/* Current Plan Overview */}
                <Card className="glass-card border-0 mb-8">
                    <CardContent className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <div className={`w-16 h-16 rounded-xl bg-gradient-to-r from-cyan-500/20 to-purple-500/20 flex items-center justify-center ${getPlanColor(subscription?.current_plan)}`}>
                                    {React.createElement(getPlanIcon(subscription?.current_plan), { className: 'w-8 h-8' })}
                                </div>
                                <div>
                                    <div className="text-sm text-white/60">Current Plan</div>
                                    <div className="text-2xl font-bold">{subscription?.plan_name}</div>
                                    <Badge className={`mt-1 ${subscription?.subscription_status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                        {subscription?.subscription_status === 'active' ? 'Active' : 'Trial'}
                                    </Badge>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm text-white/60">Monthly Cost</div>
                                <div className="text-3xl font-bold">
                                    ${subscription?.monthly_cost || 0}
                                    <span className="text-sm text-white/40">/month</span>
                                </div>
                                {subscription?.next_billing_date && (
                                    <div className="text-sm text-white/40 mt-1">
                                        Next billing: {new Date(subscription.next_billing_date).toLocaleDateString()}
                                    </div>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Usage Statistics */}
                <div className="grid md:grid-cols-4 gap-4 mb-8">
                    <Card className="glass-card border-0">
                        <CardContent className="p-4 text-center">
                            <Monitor className="w-8 h-8 text-cyan-400 mx-auto mb-2" />
                            <div className="text-2xl font-bold">{usage?.devices_protected || 0}</div>
                            <div className="text-sm text-white/60">Devices</div>
                            <Progress 
                                value={subscription?.devices_limit > 0 ? (usage?.devices_protected / subscription?.devices_limit) * 100 : 0} 
                                className="mt-2 h-1"
                            />
                            <div className="text-xs text-white/40 mt-1">
                                {usage?.devices_protected} / {subscription?.devices_limit === -1 ? 'Unlimited' : subscription?.devices_limit}
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="glass-card border-0">
                        <CardContent className="p-4 text-center">
                            <AlertTriangle className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                            <div className="text-2xl font-bold">{usage?.threats_detected || 0}</div>
                            <div className="text-sm text-white/60">Threats Detected</div>
                        </CardContent>
                    </Card>
                    <Card className="glass-card border-0">
                        <CardContent className="p-4 text-center">
                            <Brain className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                            <div className="text-2xl font-bold">{usage?.ai_analyses_performed || 0}</div>
                            <div className="text-sm text-white/60">AI Analyses</div>
                        </CardContent>
                    </Card>
                    <Card className="glass-card border-0">
                        <CardContent className="p-4 text-center">
                            <TrendingUp className="w-8 h-8 text-green-400 mx-auto mb-2" />
                            <div className="text-2xl font-bold">{usage?.uptime_percentage || 100}%</div>
                            <div className="text-sm text-white/60">Uptime</div>
                        </CardContent>
                    </Card>
                </div>

                {/* Billing Toggle */}
                <div className="flex justify-center mb-8">
                    <div className="inline-flex bg-white/5 rounded-lg p-1">
                        <button
                            onClick={() => setSelectedBilling('monthly')}
                            className={`px-6 py-2 rounded-md transition-colors ${selectedBilling === 'monthly' ? 'bg-cyan-500 text-white' : 'text-white/60 hover:text-white'}`}
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setSelectedBilling('yearly')}
                            className={`px-6 py-2 rounded-md transition-colors ${selectedBilling === 'yearly' ? 'bg-cyan-500 text-white' : 'text-white/60 hover:text-white'}`}
                        >
                            Yearly <span className="text-xs text-green-400 ml-1">Save 20%</span>
                        </button>
                    </div>
                </div>

                {/* Plans Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {plans.map((plan) => {
                        const Icon = getPlanIcon(plan.id);
                        const isCurrentPlan = subscription?.current_plan === plan.id;
                        const price = selectedBilling === 'yearly' ? Math.round(plan.price * 0.8) : plan.price;
                        
                        return (
                            <Card 
                                key={plan.id} 
                                className={`glass-card border-0 relative overflow-hidden ${
                                    isCurrentPlan ? 'ring-2 ring-cyan-500' : ''
                                } ${plan.id === 'professional' ? 'md:scale-105' : ''}`}
                            >
                                {plan.id === 'professional' && (
                                    <div className="absolute top-0 right-0 bg-gradient-to-r from-cyan-500 to-purple-600 text-white text-xs px-3 py-1 rounded-bl-lg">
                                        Popular
                                    </div>
                                )}
                                <CardHeader className="text-center pb-2">
                                    <div className={`w-12 h-12 rounded-xl mx-auto mb-3 flex items-center justify-center bg-white/5 ${getPlanColor(plan.id)}`}>
                                        <Icon className="w-6 h-6" />
                                    </div>
                                    <CardTitle className="text-lg">{plan.name}</CardTitle>
                                    <div className="text-3xl font-bold mt-2">
                                        ${price}
                                        <span className="text-sm text-white/40">/device/mo</span>
                                    </div>
                                    <div className="text-sm text-white/40">
                                        {plan.max_devices === -1 ? 'Unlimited' : `Up to ${plan.max_devices}`} devices
                                    </div>
                                </CardHeader>
                                <CardContent className="pt-4">
                                    <ul className="space-y-2 mb-6">
                                        {plan.features.map((feature, index) => (
                                            <li key={index} className="flex items-start gap-2 text-sm">
                                                <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                                <span className="text-white/80">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    {isCurrentPlan ? (
                                        <Button disabled className="w-full bg-white/10 text-white/60">
                                            Current Plan
                                        </Button>
                                    ) : (
                                        <Button
                                            onClick={() => handleUpgrade(plan.id)}
                                            disabled={processingPlan === plan.id || plan.id === 'free'}
                                            className={`w-full ${
                                                plan.id === 'professional' 
                                                    ? 'bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700' 
                                                    : 'bg-white/10 hover:bg-white/20 text-white'
                                            }`}
                                        >
                                            {processingPlan === plan.id ? (
                                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                                            ) : plan.price === 0 ? (
                                                'Free Plan'
                                            ) : (
                                                <><CreditCard className="w-4 h-4 mr-2" /> Upgrade</>
                                            )}
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Billing History */}
                <Card className="glass-card border-0">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-cyan-400" />
                            Billing History
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-center py-8 text-white/40">
                            <CreditCard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p>No billing history yet</p>
                            <p className="text-sm mt-2">Payment integration coming soon</p>
                        </div>
                    </CardContent>
                </Card>

                {/* Contact Support */}
                <div className="mt-8 text-center text-white/60">
                    <p>Need help with your subscription? Contact us at{' '}
                        <a href="mailto:support@shirosec.com" className="text-cyan-400 hover:underline">
                            support@shirosec.com
                        </a>
                    </p>
                </div>
            </main>
        </div>
    );
};

export default Subscription;
